import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Container,
  Group,
  List,
  Loader,
  MultiSelect,
  Paper,
  Stack,
  Stepper,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconArrowRight,
  IconCheck,
  IconSparkles,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useLocation } from "wouter";
import { useBrandsStore } from "./brandsStore";
import {
  activateBrand,
  importPositioning,
  suggestCategories,
} from "./brandsApi";
import { createAutomatedQueries } from "./monitorsApi";
import BrandDiscoveryProgressCard from "./BrandDiscoveryProgressCard";
import TrackingSnippet from "./TrackingSnippet";
import EditableText from "./EditableText";
import EditableTagsInput from "./EditableTagsInput";
import EditablePriorityArray from "./EditablePriorityArray";
import { supportedLanguages } from "../_Shared/LanguageSelector";

const INDUSTRY_OPTIONS = [
  "Technology",
  "Healthcare",
  "Finance",
  "Retail",
  "Manufacturing",
  "Education",
  "Entertainment",
  "Food & Beverage",
  "Automotive",
  "Real Estate",
  "Travel",
  "Sports",
  "Fashion",
  "Beauty",
  "Optics",
];

/**
 * Derive the first incomplete step from the current brand state, so refreshing mid-flow
 * lands the user back where they left off.
 */
function deriveStartStep(brand) {
  if (brand.discoveryStage && brand.discoveryStage !== "Done" && brand.discoveryStage !== "Failed") return 0;
  const hasBasics = brand.name && brand.websiteUrl && (brand.industries?.length ?? 0) > 0;
  if (!hasBasics) return 1;
  if (!brand.description && !brand.descriptionNative && !brand.valuePropositions) return 2;
  const hasInference = (brand.topics?.length ?? 0) > 0 || (brand.keywords?.length ?? 0) > 0;
  if (!hasInference) return 3;
  if ((brand.categories?.length ?? 0) === 0) return 4;
  if ((brand.trackedLlmProviders?.length ?? 0) === 0) return 5;
  return 6;
}

export default function BrandSetupWizard({ brand }) {
  const { updateBrand, refreshBrandById } = useBrandsStore();
  const [, setLocation] = useLocation();
  const [activeStep, setActiveStep] = useState(() => deriveStartStep(brand));
  const [editingField, setEditingField] = useState(null);
  const [activating, setActivating] = useState(false);

  // Step 1 auto-advance: once discovery completes, move past the "discovering" step.
  useEffect(() => {
    if (activeStep === 0 && brand.discoveryStage === "Done") setActiveStep(1);
  }, [activeStep, brand.discoveryStage]);

  const handleSaveField = useCallback(
    async (fieldName, value) => {
      if (!brand.canEdit) {
        notifications.show({
          title: "Permission denied",
          message: "You do not have permission to edit this brand",
          color: "red",
        });
        return;
      }
      try {
        await updateBrand(brand.id, { ...brand, [fieldName]: value });
        setEditingField(null);
      } catch {
        // store shows the notification
      }
    },
    [brand, updateBrand],
  );

  const canActivate =
    (brand.industries?.length ?? 0) > 0 &&
    (brand.trackedLlmProviders?.length ?? 0) > 0;

  const handleActivate = async () => {
    setActivating(true);
    try {
      await activateBrand(brand.id);
      await refreshBrandById(brand.id);
      notifications.show({
        title: "Brand activated",
        message: "Your brand is now live. The dashboard will start populating.",
        color: "green",
      });
      setLocation("/dashboard");
    } catch (err) {
      notifications.show({
        title: "Could not activate",
        message: err?.message ?? "Make sure industries and at least one monitor are configured.",
        color: "red",
      });
    } finally {
      setActivating(false);
    }
  };

  const totalSteps = 7;
  const goNext = () => setActiveStep((s) => Math.min(s + 1, totalSteps - 1));
  const goPrev = () => setActiveStep((s) => Math.max(s - 1, 0));

  return (
    <Container size="md" mt="md">
      <Stack gap="md">
        <Group justify="space-between">
          <div>
            <Title order={2}>Set up your brand</Title>
            <Text size="sm" c="dimmed">
              Identity, site, and what we ask AI. Writing voice moves to Brand Kit once this brand is Active.
            </Text>
          </div>
          <Text size="sm" c="dimmed">
            Step {activeStep + 1} of {totalSteps}
          </Text>
        </Group>

        <Stepper
          active={activeStep}
          onStepClick={setActiveStep}
          allowNextStepsSelect={false}
          size="sm"
        >
          <Stepper.Step label="Discover" />
          <Stepper.Step label="Basics" />
          <Stepper.Step label="Positioning" />
          <Stepper.Step label="Inferred details" />
          <Stepper.Step label="Categories" />
          <Stepper.Step label="Monitors" />
          <Stepper.Step label="Activate" />
        </Stepper>

        <Paper withBorder p="md" radius="md">
          {activeStep === 0 && (
            <DiscoveryStep brand={brand} onRefresh={() => refreshBrandById(brand.id)} />
          )}
          {activeStep === 1 && (
            <BasicsStep
              brand={brand}
              editingField={editingField}
              setEditingField={setEditingField}
              onSave={handleSaveField}
            />
          )}
          {activeStep === 2 && (
            <PositioningStep brand={brand} onSave={handleSaveField} />
          )}
          {activeStep === 3 && (
            <InferredDetailsStep
              brand={brand}
              editingField={editingField}
              setEditingField={setEditingField}
              onSave={handleSaveField}
            />
          )}
          {activeStep === 4 && (
            <CategoriesStep
              brand={brand}
              editingField={editingField}
              setEditingField={setEditingField}
              onSave={handleSaveField}
            />
          )}
          {activeStep === 5 && (
            <MonitorsStep
              brand={brand}
              editingField={editingField}
              setEditingField={setEditingField}
              onSave={handleSaveField}
              onProvidersUpdated={() => refreshBrandById(brand.id)}
            />
          )}
          {activeStep === 6 && (
            <ActivateStep
              brand={brand}
              canActivate={canActivate}
              activating={activating}
              onActivate={handleActivate}
            />
          )}
        </Paper>

        <Group justify="space-between">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={goPrev}
            disabled={activeStep === 0}
          >
            Back
          </Button>
          {activeStep < totalSteps - 1 ? (
            <Button rightSection={<IconArrowRight size={16} />} onClick={goNext}>
              Continue
            </Button>
          ) : (
            <Button
              color="teal"
              leftSection={<IconCheck size={16} />}
              onClick={handleActivate}
              loading={activating}
              disabled={!canActivate || !brand.canEdit}
            >
              Activate brand
            </Button>
          )}
        </Group>
      </Stack>
    </Container>
  );
}

function DiscoveryStep({ brand, onRefresh }) {
  const isActive =
    !!brand.currentDiscoveryJobId &&
    brand.discoveryStage !== "Done" &&
    brand.discoveryStage !== "Failed";
  return (
    <Stack gap="md">
      <Title order={4}>Discovering your brand</Title>
      <Text size="sm" c="dimmed">
        We're analyzing your website to infer your topics, keywords, competitors, and markets.
        You can continue editing the steps below while we work.
      </Text>
      {(isActive || brand.discoveryStage === "Failed") ? (
        <BrandDiscoveryProgressCard brand={brand} onComplete={onRefresh} />
      ) : (
        <Alert color="teal" icon={<IconCheck size={18} />} title="Discovery complete">
          Found {brand.topics?.length ?? 0} topics, {brand.keywords?.length ?? 0} keywords,
          {" "}
          {brand.competitors?.length ?? 0} competitors. Continue to review and confirm.
        </Alert>
      )}
    </Stack>
  );
}

function BasicsStep({ brand, editingField, setEditingField, onSave }) {
  return (
    <Stack gap="md">
      <Title order={4}>The basics</Title>
      <Text size="sm" c="dimmed">
        Just a few things to confirm. Industries unlocks the AI features in later steps.
      </Text>
      <EditableText
        value={brand.name}
        fieldName="name"
        placeholder="Brand name"
        editingField={editingField}
        setEditingField={setEditingField}
        onSave={onSave}
        disabled={!brand.canEdit}
      />
      <EditableText
        value={brand.websiteUrl}
        fieldName="websiteUrl"
        placeholder="Website URL (e.g. https://example.com)"
        editingField={editingField}
        setEditingField={setEditingField}
        onSave={onSave}
        disabled={!brand.canEdit}
      />
      <div>
        <Text size="sm" fw={500} mb={4}>
          Industries
        </Text>
        <EditableTagsInput
          value={brand.industries}
          fieldName="industries"
          editingField={editingField}
          setEditingField={setEditingField}
          onSave={onSave}
          data={INDUSTRY_OPTIONS}
          placeholder="Click to add industries"
          hideTitle
          disabled={!brand.canEdit}
        />
      </div>
      <EditableText
        value={brand.descriptionNative}
        fieldName="descriptionNative"
        placeholder="Short description (1-2 sentences)"
        multiline
        editingField={editingField}
        setEditingField={setEditingField}
        onSave={onSave}
        disabled={!brand.canEdit}
      />
    </Stack>
  );
}

function PositioningStep({ brand, onSave }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  const runImport = async () => {
    if (!text.trim()) return;
    setLoading(true);
    setSuggestion(null);
    try {
      const result = await importPositioning(brand.id, text);
      setSuggestion(result);
    } catch (err) {
      notifications.show({
        title: "Import failed",
        message: err?.message ?? "Couldn't interpret the pasted text",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  const applySuggestion = async () => {
    if (!suggestion) return;
    await onSave("descriptionNative", suggestion.description);
    await onSave("valuePropositions", suggestion.valuePropositions);
    if (suggestion.suggestedTopics?.length > 0) {
      const merged = [...(brand.topics ?? [])];
      for (const t of suggestion.suggestedTopics) {
        if (!merged.find((x) => x.name === t.name)) merged.push(t);
      }
      await onSave("topics", merged);
    }
    notifications.show({
      title: "Positioning applied",
      message: "Description, value props, and topics updated. You can edit them in the next steps.",
      color: "green",
    });
    setSuggestion(null);
    setText("");
  };

  return (
    <Stack gap="md">
      <Title order={4}>Positioning &amp; ICP <Badge ml="xs" size="sm" variant="light">Optional</Badge></Title>
      <Text size="sm" c="dimmed">
        Have a positioning doc, ICP brief, or About page? Paste anything that describes who you serve and how.
        We distill it into a monitor description and value propositions. After this brand is Active, writing voice lives in Brand Kit.
      </Text>
      <Textarea
        placeholder="Paste your positioning, ICP, or About page text…"
        autosize
        minRows={5}
        maxRows={14}
        value={text}
        onChange={(e) => setText(e.currentTarget.value)}
        disabled={!brand.canEdit || loading}
      />
      <Group justify="space-between">
        <Text size="xs" c="dimmed">
          You can skip this step and edit individual fields manually instead.
        </Text>
        <Button
          leftSection={<IconSparkles size={16} />}
          onClick={runImport}
          loading={loading}
          disabled={!brand.canEdit || !text.trim()}
        >
          Distill with AI
        </Button>
      </Group>
      {suggestion && (
        <Alert color="violet" icon={<IconSparkles size={18} />} title="Suggested updates">
          <Stack gap="xs">
            <div>
              <Text size="xs" fw={600}>Description</Text>
              <Text size="sm">{suggestion.description}</Text>
            </div>
            <div>
              <Text size="xs" fw={600}>Value propositions</Text>
              <Text size="sm">{suggestion.valuePropositions}</Text>
            </div>
            <div>
              <Text size="xs" fw={600}>Ideal customer</Text>
              <Text size="sm">{suggestion.idealCustomerProfile}</Text>
            </div>
            {suggestion.suggestedTopics?.length > 0 && (
              <div>
                <Text size="xs" fw={600}>Suggested topics</Text>
                <Group gap={6}>
                  {suggestion.suggestedTopics.map((t) => (
                    <Badge key={t.name} variant="light">{t.name}</Badge>
                  ))}
                </Group>
              </div>
            )}
            <Group justify="flex-end" mt="xs">
              <Button variant="subtle" size="xs" onClick={() => setSuggestion(null)}>
                Discard
              </Button>
              <Button size="xs" onClick={applySuggestion}>Apply suggestion</Button>
            </Group>
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}

function InferredDetailsStep({ brand, editingField, setEditingField, onSave }) {
  return (
    <Stack gap="md">
      <Title order={4}>Review what we found</Title>
      <Text size="sm" c="dimmed">
        These were inferred from your website during discovery. Tweak anything that looks off — they
        feed the monitoring prompts and content generation.
      </Text>
      <EditablePriorityArray
        value={brand.topics}
        fieldName="topics"
        editingField={editingField}
        setEditingField={setEditingField}
        onSave={onSave}
        title="Topics / core aspects"
        modalTitle="Add/Edit topic"
        nameLabel="Topic name"
        priorityLabel="Priority"
        disabled={!brand.canEdit}
      />
      <EditableTagsInput
        value={brand.keywords}
        fieldName="keywords"
        editingField={editingField}
        setEditingField={setEditingField}
        onSave={onSave}
        placeholder="Click to add keywords"
        disabled={!brand.canEdit}
      />
      <EditablePriorityArray
        value={brand.competitors}
        fieldName="competitors"
        editingField={editingField}
        setEditingField={setEditingField}
        onSave={onSave}
        title="Competitors"
        modalTitle="Add/Edit competitor"
        nameLabel="Competitor name"
        websiteLabel="Website URL"
        priorityLabel="Priority"
        disabled={!brand.canEdit}
      />
    </Stack>
  );
}

function CategoriesStep({ brand, editingField, setEditingField, onSave }) {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);

  const fetchSuggestions = async () => {
    setLoading(true);
    setSuggestions(null);
    try {
      const result = await suggestCategories(brand.id);
      setSuggestions(result.categories ?? []);
    } catch (err) {
      notifications.show({
        title: "Could not suggest categories",
        message: err?.message ?? "Try again in a moment.",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  const acceptAll = async () => {
    if (!suggestions?.length) return;
    const existing = brand.categories ?? [];
    const merged = [...existing];
    for (const c of suggestions) {
      if (!merged.find((x) => x.name === c.name)) merged.push(c);
    }
    await onSave("categories", merged);
    setSuggestions(null);
  };

  return (
    <Stack gap="md">
      <Title order={4}>Categories <Badge ml="xs" size="sm" variant="light">Optional</Badge></Title>
      <Text size="sm" c="dimmed">
        Categories describe what you sell or serve — they're used to focus monitoring queries.
        Want help? Have AI suggest based on what we know about your brand.
      </Text>
      <EditablePriorityArray
        value={brand.categories}
        fieldName="categories"
        editingField={editingField}
        setEditingField={setEditingField}
        onSave={onSave}
        title="Your categories"
        modalTitle="Add/Edit category"
        nameLabel="Category name"
        priorityLabel="Priority"
        disabled={!brand.canEdit}
      />
      <Group>
        <Button
          variant="light"
          leftSection={<IconSparkles size={16} />}
          onClick={fetchSuggestions}
          loading={loading}
          disabled={!brand.canEdit}
        >
          Suggest with AI
        </Button>
      </Group>
      {suggestions && suggestions.length > 0 && (
        <Alert color="violet" icon={<IconSparkles size={18} />} title="AI suggestions">
          <Stack gap="xs">
            <Group gap={6}>
              {suggestions.map((c) => (
                <Badge key={c.name} variant="light" size="md">{c.name}</Badge>
              ))}
            </Group>
            <Group justify="flex-end" mt="xs">
              <Button variant="subtle" size="xs" onClick={() => setSuggestions(null)}>
                Discard
              </Button>
              <Button size="xs" onClick={acceptAll}>Add all</Button>
            </Group>
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}

function MonitorsStep({ brand, onSave, onProvidersUpdated }) {
  const [, setLocation] = useLocation();
  const [creating, setCreating] = useState(false);

  const handleQuickSetup = async () => {
    setCreating(true);
    try {
      await createAutomatedQueries(brand.id);
      await onProvidersUpdated?.();
      notifications.show({
        title: "Monitors created",
        message: "Default LLM provider monitors are set up. You can fine-tune them on the monitors page.",
        color: "green",
      });
    } catch (err) {
      notifications.show({
        title: "Could not create monitors",
        message: err?.message ?? "Try again or set them up manually.",
        color: "red",
      });
    } finally {
      setCreating(false);
    }
  };

  const tracked = brand.trackedLlmProviders ?? [];
  const targetLanguages = brand.targetLanguages ?? [];
  const anchorLang = supportedLanguages.find(l => l.value === brand.defaultTargetLanguageCode);

  return (
    <Stack gap="md">
      <Title order={4}>Connect your monitors</Title>
      <Text size="sm" c="dimmed">
        We monitor your brand's visibility across LLM providers. You need at least one to activate.
      </Text>

      <Paper withBorder p="sm">
        <Group justify="space-between">
          <div>
            <Text size="sm" fw={500}>LLM providers</Text>
            <Text size="xs" c="dimmed">
              {tracked.length === 0
                ? "No providers tracked yet."
                : `${tracked.length} provider${tracked.length === 1 ? "" : "s"} connected.`}
            </Text>
          </div>
          <Group>
            <Button variant="subtle" size="xs" onClick={() => setLocation("/llm-config?tab=monitors")}>
              Open monitor config
            </Button>
            {tracked.length === 0 && (
              <Button
                size="xs"
                leftSection={<IconSparkles size={14} />}
                onClick={handleQuickSetup}
                loading={creating}
                disabled={!brand.canEdit}
              >
                Quick setup
              </Button>
            )}
          </Group>
        </Group>
      </Paper>

      <Paper withBorder p="sm">
        <Text size="sm" fw={500} mb={4}>Target languages <Badge ml="xs" size="xs" variant="light">Optional</Badge></Text>
        <Text size="xs" c="dimmed" mb="xs">
          Anchor language: {anchorLang?.label ?? brand.defaultTargetLanguageCode}
        </Text>
        <MultiSelect
          searchable
          placeholder="Add languages we should also monitor and translate to"
          data={supportedLanguages.filter(l => l.value !== brand.defaultTargetLanguageCode)}
          value={targetLanguages}
          onChange={(value) => onSave("targetLanguages", value)}
          disabled={!brand.canEdit}
        />
      </Paper>
    </Stack>
  );
}

function ActivateStep({ brand, canActivate, activating, onActivate }) {
  const requirements = [
    {
      label: "At least one industry set",
      met: (brand.industries?.length ?? 0) > 0,
    },
    {
      label: "At least one LLM monitor connected",
      met: (brand.trackedLlmProviders?.length ?? 0) > 0,
    },
  ];

  return (
    <Stack gap="md">
      <Title order={4}>Activate brand</Title>
      <Text size="sm" c="dimmed">
        Once you activate, monitoring runs on its regular cadence and your dashboard fills in with data.
        You can come back and edit any of this from the brand settings.
      </Text>
      <Paper withBorder p="sm">
        <Text size="sm" fw={500} mb="xs">Ready to go?</Text>
        <List spacing="xs">
          {requirements.map((req) => (
            <List.Item
              key={req.label}
              icon={
                req.met
                  ? <IconCheck size={14} color="var(--mantine-color-teal-6)" />
                  : <Loader size={14} />
              }
            >
              <Text size="sm" c={req.met ? undefined : "dimmed"}>
                {req.label}
              </Text>
            </List.Item>
          ))}
        </List>
      </Paper>
      <Group justify="flex-end">
        <Button
          color="teal"
          leftSection={<IconCheck size={16} />}
          onClick={onActivate}
          loading={activating}
          disabled={!canActivate || !brand.canEdit}
        >
          Activate brand
        </Button>
      </Group>

      {/* Installing the pixel is independent of activation, and the sooner it goes up the
          sooner there is traffic to show. It used to live only on the settings page, which
          renders exclusively for Active brands — so the brands that most needed it could
          not reach it. */}
      <TrackingSnippet brand={brand} />
    </Stack>
  );
}
